import { callRpc, callAuthed } from './client';
import type {
  AcceptTermsResult,
  Album,
  AnswerQuizResult,
  CreateListingResult,
  GameState,
  LoginResult,
  MakeOfferResult,
  SubmitResult,
  TeamPublic,
  UseCardResult,
} from './types';

/** log_pings の配列要素。client_at が無い要素は SQL 側で捨てられる */
export interface PingInput {
  seq?: number;
  /** ISO。必須 */
  client_at: string;
  lat?: number | null;
  lon?: number | null;
  acc?: number | null;
  visibility?: string | null;
  online?: boolean | null;
  battery?: number | null;
}

/** log_map_views の配列要素。範囲外（緯度経度・zoom 0-24・s<=n）は SQL 側で捨てられる */
export interface MapViewInput {
  /** ISO。必須 */
  client_at: string;
  n: number;
  s: number;
  e: number;
  w: number;
  zoom: number;
}

export type TargetKind = 'spot' | 'prompt';

export function getTeamsPublic(eventId: string): Promise<TeamPublic[]> {
  return callRpc<TeamPublic[]>('get_teams_public', { p_event_id: eventId });
}

export function login(teamId: string, pin: string, deviceId: string): Promise<LoginResult> {
  return callRpc<LoginResult>('login', { p_team_id: teamId, p_pin: pin, p_device_id: deviceId });
}

export interface AcceptTermsArgs {
  termsVersion: string;
  publicityOptIn: boolean;
  nickname?: string | null;
  userAgent?: string | null;
}

export function acceptTerms(a: AcceptTermsArgs): Promise<AcceptTermsResult> {
  return callAuthed<AcceptTermsResult>('accept_terms', {
    p_terms_version: a.termsVersion,
    p_publicity_opt_in: a.publicityOptIn,
    p_nickname: a.nickname ?? null,
    p_user_agent: a.userAgent ?? null,
  });
}

export interface LogViewOpenArgs {
  targetKind: TargetKind;
  targetId: number;
  clientOpenedAt?: string | null;
  lat?: number | null;
  lon?: number | null;
  acc?: number | null;
  openedFrom?: 'map' | 'book' | 'prompts' | null;
}

/** 戻り値は view_id（logViewClose に渡す） */
export function logViewOpen(a: LogViewOpenArgs): Promise<number> {
  return callAuthed<number>('log_view_open', {
    p_target_kind: a.targetKind,
    p_target_id: a.targetId,
    p_client_opened_at: a.clientOpenedAt ?? null,
    p_lat: a.lat ?? null,
    p_lon: a.lon ?? null,
    p_acc: a.acc ?? null,
    p_opened_from: a.openedFrom ?? null,
  });
}

export interface LogViewCloseArgs {
  viewId: number;
  clientClosedAt?: string | null;
  reason?: string | null;
}

export function logViewClose(a: LogViewCloseArgs): Promise<void> {
  return callAuthed<void>('log_view_close', {
    p_view_id: a.viewId,
    p_client_closed_at: a.clientClosedAt ?? null,
    p_reason: a.reason ?? null,
  });
}

/** 戻り値は新規に入った件数（最大500件/回） */
export function logPings(pings: PingInput[]): Promise<number> {
  return callAuthed<number>('log_pings', { p_pings: pings });
}

/** 戻り値は新規に入った件数（最大500件/回） */
export function logMapViews(views: MapViewInput[]): Promise<number> {
  return callAuthed<number>('log_map_views', { p_views: views });
}

export interface SubmitArgs {
  kind: TargetKind;
  targetId: number;
  imagePath: string;
  clientActionAt?: string | null;
  lat?: number | null;
  lon?: number | null;
  acc?: number | null;
  exifLat?: number | null;
  exifLon?: number | null;
}

export function submit(a: SubmitArgs): Promise<SubmitResult> {
  return callAuthed<SubmitResult>('submit', {
    p_kind: a.kind,
    p_target_id: a.targetId,
    p_image_path: a.imagePath,
    p_client_action_at: a.clientActionAt ?? null,
    p_lat: a.lat ?? null,
    p_lon: a.lon ?? null,
    p_acc: a.acc ?? null,
    p_exif_lat: a.exifLat ?? null,
    p_exif_lon: a.exifLon ?? null,
  });
}

export interface AnswerQuizArgs {
  promptId: number;
  /** 1始まり */
  choice: number;
  clientActionAt?: string | null;
  lat?: number | null;
  lon?: number | null;
  acc?: number | null;
}

export function answerQuiz(a: AnswerQuizArgs): Promise<AnswerQuizResult> {
  return callAuthed<AnswerQuizResult>('answer_quiz', {
    p_prompt_id: a.promptId,
    p_choice: a.choice,
    p_client_action_at: a.clientActionAt ?? null,
    p_lat: a.lat ?? null,
    p_lon: a.lon ?? null,
    p_acc: a.acc ?? null,
  });
}

export interface UseCardArgs {
  teamCardId: number;
  /** steal / block_area / duel で必須 */
  targetTeam?: string | null;
  /** multiplier_area / block_area / build で必須 */
  areaId?: string | null;
}

export function useCard(a: UseCardArgs): Promise<UseCardResult> {
  return callAuthed<UseCardResult>('use_card', {
    p_team_card_id: a.teamCardId,
    p_target_team: a.targetTeam ?? null,
    p_area_id: a.areaId ?? null,
  });
}

export interface CreateListingArgs {
  promptId: number;
  /** wantPromptId と wantAreaId は、ちょうど一方だけ指定する */
  wantPromptId?: number | null;
  wantAreaId?: string | null;
}

export function createListing(a: CreateListingArgs): Promise<CreateListingResult> {
  return callAuthed<CreateListingResult>('create_listing', {
    p_prompt_id: a.promptId,
    p_want_prompt_id: a.wantPromptId ?? null,
    p_want_area_id: a.wantAreaId ?? null,
  });
}

export function withdrawListing(): Promise<void> {
  return callAuthed<void>('withdraw_listing', {});
}

export function makeOffer(listingId: number, promptId: number): Promise<MakeOfferResult> {
  return callAuthed<MakeOfferResult>('make_offer', { p_listing_id: listingId, p_prompt_id: promptId });
}

export function getState(): Promise<GameState> {
  return callAuthed<GameState>('get_state', {});
}

export function getAlbum(): Promise<Album> {
  return callAuthed<Album>('get_album', {});
}
